import { LineString } from "geojson";
import { RouteMetadata, RouteSegmentPointsList } from "../../generated/gpb/api/route";
import {
    addPointToRoute,
    generatePoints,
    generateRiskChangeList,
    PointCategory,
    renderRouteSources,
    ROUTE_DISPLAY_TYPES
} from "./geoUtils";
import React, { useEffect, useMemo, useState } from "react";
import { Layer, MapRef, Popup, Source } from "react-map-gl";
import { useAllTaus } from "../../features/surface/surfaceSlice";
import { Box, Typography } from "@mui/material";
import {
    BEYOND_FORECAST_POPUP_HEADER,
    BEYOND_FORECAST_POPUP_TEXT,
    OUTSIDE_FORECAST_POPUP_HEADER,
    OUTSIDE_FORECAST_POPUP_TEXT
} from "../UserVisibleStrings";
import { IsPointWithinImpactSurface } from "../metric/RouteUtils";
import { useIsShowingMarkerPopup, useOpacity } from "../../features/mapDisplay/mapDisplaySlice";
import { RiskCategory } from "../../generated/gpb/api/risk";
import { RouteViewState, useRouteViewState } from "../../features/riskViewer/riskViewerSlice";
import { createBackgroundRouteLayer } from "./GeoViewStyle";

type GeoPointsProps = {
    mapRef: React.RefObject<MapRef>,
    routePoints: RouteSegmentPointsList,
    routeNumber: number,
    isSelected: boolean,
    routeMetadata: RouteMetadata | undefined,
};
/**
 * Component to generate points for a route
 */
export const GeoPoints: React.FC<GeoPointsProps> = ( { mapRef, routePoints, routeNumber, isSelected } ) =>
{
    const points = useMemo( () => generatePoints( routePoints ), [ routePoints ] );
    const riskChanges = useMemo( () => generateRiskChangeList( routePoints ), [ routePoints ] );
    const taus = useAllTaus();
    const routeViewState = useRouteViewState();
    const userSetOpacity = useOpacity();
    const isShowingMarkerPopup = useIsShowingMarkerPopup();
    const lineOpacity = routeViewState === RouteViewState.COLORED_ROUTE ? 1 : userSetOpacity;

    const [
        routes,
        postForecastRoutes,
        outsideOfWilmaSurfaceRoutes,
        goldilocksRoutes,
        noImpactRoutes,
        levelOneRoutes,
        levelTwoRoutes,
        levelThreeRoutes,
        levelFourRoutes,
        levelFiveRoutes,
        beyondForecastIds,
        outsideOfWilmaSurfaceIds,
    ] = useMemo( () =>
    {
        const categoryMap: Record<PointCategory, Array<LineString>> = {
            [PointCategory.inForecast]: [],
            [PointCategory.postForecast]: [],
            [PointCategory.outsideForecast]: [],
            [PointCategory.goldilocks]: [],
            [PointCategory.none]: [],
            [PointCategory.levelOne]: [],
            [PointCategory.levelTwo]: [],
            [PointCategory.levelThree]: [],
            [PointCategory.levelFour]: [],
            [PointCategory.levelFive]: [],
        };
        const beyondForecastIdArray = new Array<string>();
        const outsideOfWilmaSurfaceIdArray = new Array<string>();
        let lastObsevedPointCategory: PointCategory | undefined = undefined;

        points?.forEach( ( point, index ) =>
        {
            const timestamp = point.timeStamp?.getTime() ?? 0;
            const inSurface = IsPointWithinImpactSurface( point.latitude, point.longitude );

            const add = ( category: PointCategory ) =>
            {
                lastObsevedPointCategory = addPointToRoute(
                    lastObsevedPointCategory,
                    category,
                    categoryMap[category],
                    point,
                    categoryMap
                );
            };


            if ( timestamp > taus[taus.length - 1] )
            {
                add( PointCategory.postForecast );
            }
            else
            {
                const getRiskChange = ( risk: RiskCategory ) => riskChanges?.some( change => change.index === index && change.risk === risk ) === true;

                const isUnchanged = ( category: PointCategory ) => lastObsevedPointCategory === category &&
                    !( riskChanges?.flatMap( change => change.index ).includes( index ) === true );

                if ( routeViewState === RouteViewState.ROUTE_WILMA )
                {
                    if ( inSurface )
                    {
                        add( PointCategory.inForecast );
                    }
                    else
                    {
                        add( PointCategory.outsideForecast );
                    }
                }
                else if ( isUnchanged( PointCategory.goldilocks ) || getRiskChange( RiskCategory.RISK_CATEGORY_GOLDILOCKS ) )
                {
                    add( PointCategory.goldilocks );
                }
                else if ( isUnchanged( PointCategory.none ) || getRiskChange( RiskCategory.RISK_CATEGORY_NONE ) )
                {
                    add( PointCategory.none );
                }
                else if ( isUnchanged( PointCategory.levelOne ) || getRiskChange( RiskCategory.RISK_CATEGORY_LOW ) )
                {
                    add( PointCategory.levelOne );
                }
                else if ( isUnchanged( PointCategory.levelTwo ) || getRiskChange( RiskCategory.RISK_CATEGORY_LOW_MEDIUM ) )
                {
                    add( PointCategory.levelTwo );
                }
                else if ( isUnchanged( PointCategory.levelThree ) || getRiskChange( RiskCategory.RISK_CATEGORY_MEDIUM ) )
                {
                    add( PointCategory.levelThree );
                }
                else if ( isUnchanged( PointCategory.levelFour ) || getRiskChange( RiskCategory.RISK_CATEGORY_MEDIUM_HIGH ) )
                {
                    add( PointCategory.levelFour );
                }
                else if ( isUnchanged( PointCategory.levelFive ) || getRiskChange( RiskCategory.RISK_CATEGORY_HIGH ) )
                {
                    add( PointCategory.levelFive );
                }
                else if ( isUnchanged( PointCategory.outsideForecast ) || getRiskChange( RiskCategory.RISK_CATEGORY_UNSPECIFIED ) )
                {
                    add( PointCategory.outsideForecast );
                }
                else
                {
                    add( PointCategory.inForecast );
                }
            }
        } );

        // Generate ID arrays
        categoryMap[PointCategory.postForecast].forEach( ( _, index ) =>
        {
            beyondForecastIdArray.push( `route-layer-post-${routeNumber}-${index}` );
        } );

        categoryMap[PointCategory.outsideForecast].forEach( ( _, index ) =>
        {
            outsideOfWilmaSurfaceIdArray.push( `route-layer-outside-${routeNumber}-${index}` );
        } );
        return [
            categoryMap[PointCategory.inForecast],
            categoryMap[PointCategory.postForecast],
            categoryMap[PointCategory.outsideForecast],
            categoryMap[PointCategory.goldilocks],
            categoryMap[PointCategory.none],
            categoryMap[PointCategory.levelOne],
            categoryMap[PointCategory.levelTwo],
            categoryMap[PointCategory.levelThree],
            categoryMap[PointCategory.levelFour],
            categoryMap[PointCategory.levelFive],
            beyondForecastIdArray,
            outsideOfWilmaSurfaceIdArray,
        ];
    }, [ points, routeNumber, routeViewState, riskChanges, taus ] );

    const [ popup, setPopup ] = useState<{ lng: number, lat: number, isOutsideSurface: boolean } | null>( null );

    useEffect( () =>
    {
        const map = mapRef.current?.getMap();
        if ( map === undefined ) return;

        const handleMouseEnterBeyondForecast = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const coordinates = e.lngLat;
            setPopup( {
                lng: coordinates.lng,
                lat: coordinates.lat,
                isOutsideSurface: false,
            } );
        };

        const handleMouseEnterOutsideWilma = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const coordinates = e.lngLat;
            setPopup( {
                lng: coordinates.lng,
                lat: coordinates.lat,
                isOutsideSurface: true,
            } );
        };

        const handleMouseLeave = () =>
        {
            setPopup( null );
        };

        if ( isSelected )
        {
            map.on( "mouseenter", beyondForecastIds, handleMouseEnterBeyondForecast );
            map.on( "mouseenter", outsideOfWilmaSurfaceIds, handleMouseEnterOutsideWilma );
            map.on( "mouseleave", beyondForecastIds.concat( outsideOfWilmaSurfaceIds ), handleMouseLeave );
        }

    }, [ mapRef, outsideOfWilmaSurfaceIds, beyondForecastIds ] );

    return (
        <React.Fragment>
            {points !== null && isSelected &&
                <Source
                    key = {`route-source-background-${routeNumber}`}
                    id = {`route-data-background-${routeNumber}`}
                    type = "geojson"
                    data = {{ type: "LineString", coordinates: points.map( point => [ point.longitude, point.latitude ] ) }}
                >
                    <Layer {...createBackgroundRouteLayer( routeViewState === RouteViewState.COLORED_ROUTE ? lineOpacity : 0, true )}/>
                    <Layer {...createBackgroundRouteLayer( routeViewState === RouteViewState.COLORED_ROUTE ? lineOpacity : 0, false )}/>
                </Source>}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.ROUTE, routes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.POST, postForecastRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.OUTSIDE, outsideOfWilmaSurfaceRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.GOLDILOCKS, goldilocksRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.NO_IMPACT, noImpactRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.LEVEL_ONE, levelOneRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.LEVEL_TWO, levelTwoRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.LEVEL_THREE, levelThreeRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.LEVEL_FOUR, levelFourRoutes, routeNumber, isSelected, lineOpacity )}
            {renderRouteSources( ROUTE_DISPLAY_TYPES.LEVEL_FIVE, levelFiveRoutes, routeNumber, isSelected, lineOpacity )}
            {popup && !isShowingMarkerPopup && (
                <Popup
                    latitude = {popup.lat}
                    longitude = {popup.lng}
                    closeButton = {false}
                    closeOnClick = {false}
                    style = {{ padding: "0px" }}
                    className = "custom-Thumbnail-Beyond-Forecast"
                >
                    <Box sx = {{ textAlign: "center", padding: "2px" }}>
                        <Typography sx = {{ color: "white", fontWeight: "bold", fontSize: "14px" }}>
                            {popup.isOutsideSurface ? OUTSIDE_FORECAST_POPUP_HEADER : BEYOND_FORECAST_POPUP_HEADER}
                        </Typography>
                        <Typography sx = {{ color: "white", fontWeight: "normal", fontSize: "12px" }}>
                            {popup.isOutsideSurface ? OUTSIDE_FORECAST_POPUP_TEXT : BEYOND_FORECAST_POPUP_TEXT}
                        </Typography>
                    </Box>
                </Popup>

            )}
        </React.Fragment>
    );
};
